import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  CheckCompany, 
  CheckOnboardingIssue, 
  CheckOnboardingIssueType,
  CheckOnboardingStatus
} from "winslow-core";
import { 
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Landmark,
  Settings,
  FileText,
  Info
} from "lucide-react";
import { PAY_CONFIGURATION_TABS, TAB_NUMBERS } from "./payConfigurationConstants";
import "./AppPayCompanyOnboardingStatus.css";

type AppPayCompanyOnboardingStatusProps = {
  checkCompany: CheckCompany;
  onResolve?: () => void;
};

type OnboardingItem = {
  id: string;
  type: CheckOnboardingIssueType;
  name: string;
  isBlocking: boolean;
  status: "blocking" | "needs_attention" | "completed";
};

// Internal component for rendering the status content (used both inline and in modal)
const OnboardingStatusContent = ({
  checkCompany,
  showInModal = false,
  onResolve,
}: {
  checkCompany: CheckCompany;
  showInModal?: boolean;
  onResolve?: () => void;
}) => {
  const navigate = useNavigate();
  const { onboard } = checkCompany;

  // Collect all items that need attention (not completed)
  const getItemsNeedingAttention = (): OnboardingItem[] => {
    const items: OnboardingItem[] = [];

    // Process bank_account - it's an array of status strings, not issues
    // If bank_account is in remaining_steps or blocking_steps, it needs attention
    if (onboard.remaining_steps.includes("bank_account") || onboard.blocking_steps.includes("bank_account")) {
      const bankAccountStatuses = onboard.bank_account || [];
      const isBlocking = onboard.blocking_steps.includes("bank_account");
      
      // Create an item for bank account if it's not completed
      // Check if we have the required statuses
      const hasRequiredStatus = bankAccountStatuses.includes("bank_account_debit_authorization_submitted");
      
      if (!hasRequiredStatus) {
        items.push({
          id: "bank_account",
          type: "bank_account",
          name: bankAccountStatuses.length > 0 
            ? `Bank Account: ${bankAccountStatuses.map(s => s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())).join(", ")}`
            : "Bank Account setup required",
          isBlocking,
          status: isBlocking ? "blocking" : "needs_attention",
        });
      }
    }

    // Process setup_parameters issues
    if (onboard.setup_parameters && onboard.setup_parameters.length > 0) {
      onboard.setup_parameters.forEach((issue: CheckOnboardingIssue) => {
        if (issue.status !== "completed") {
          items.push({
            id: issue.id,
            type: "setup_parameters",
            name: issue.name,
            isBlocking: issue.status === "blocking" || onboard.blocking_steps.includes("setup_parameters"),
            status: issue.status,
          });
        }
      });
    }

    // Process filing_authorization issues
    if (onboard.filing_authorization && onboard.filing_authorization.length > 0) {
      onboard.filing_authorization.forEach((issue: CheckOnboardingIssue) => {
        if (issue.status !== "completed") {
          items.push({
            id: issue.id,
            type: "filing_authorization",
            name: issue.name,
            isBlocking: issue.status === "blocking" || onboard.blocking_steps.includes("filing_authorization"),
            status: issue.status,
          });
        }
      });
    }

    return items;
  };

  const itemsNeedingAttention = getItemsNeedingAttention();

  const getStatusMessage = () => {
    if (onboard.status === "completed") {
      return {
        type: "success",
        icon: CheckCircle2,
        message: "Onboarding completed successfully",
        className: "onboarding-status-completed",
      };
    }

    if (onboard.status === "blocking") {
      return {
        type: "error",
        icon: XCircle,
        message: "Onboarding is blocked. Please resolve the blocking issues below.",
        className: "onboarding-status-blocked",
      };
    }

    if (onboard.status === "needs_attention") {
      return {
        type: "warning",
        icon: AlertTriangle,
        message: "Onboarding needs attention. Please review the items below.",
        className: "onboarding-status-warning",
      };
    }

    return null;
  };

  const getIssueTypeLabel = (type: CheckOnboardingIssueType): string => {
    switch (type) {
      case "bank_account":
        return "Bank Account";
      case "setup_parameters":
        return "Setup Parameters";
      case "filing_authorization":
        return "Filing Authorization";
      default:
        return type;
    }
  };

  const getIssueTypeIcon = (type: CheckOnboardingIssueType) => {
    switch (type) {
      case "bank_account":
        return Landmark;
      case "setup_parameters":
        return Settings;
      case "filing_authorization":
        return FileText;
      default:
        return null;
    }
  };

  const handleResolveClick = (type: CheckOnboardingIssueType) => {
    // Close modal if open
    if (onResolve) {
      onResolve();
    }
    
    let tab: string;
    switch (type) {
      case "bank_account":
        tab = PAY_CONFIGURATION_TABS.BANK_ACCOUNTS;
        break;
      case "setup_parameters":
        tab = PAY_CONFIGURATION_TABS.COMPANY;
        break;
      case "filing_authorization":
        tab = PAY_CONFIGURATION_TABS.FILINGS;
        break;
      default:
        tab = PAY_CONFIGURATION_TABS.COMPANY;
    }
    
    // Navigate with tab number as query parameter
    const tabNumber = TAB_NUMBERS[tab];
    if (tabNumber) {
      navigate(`/app/pay-configuration?tab=${tabNumber}`);
    } else {
      navigate(`/app/pay-configuration?tab=${tab}`);
    }
  };

  const statusConfig = getStatusMessage();

  if (!statusConfig) {
    return null;
  }

  const StatusIcon = statusConfig.icon;

  console.log("[AppPayCompanyOnboardingStatus] checkCompany: ", checkCompany);

  return (
    <div className={`app-pay-company-onboarding-status ${showInModal ? "onboarding-status-modal" : ""}`}>
      {showInModal && (
        <div className="onboarding-status-modal-header">
          <h2 className="onboarding-status-modal-title">Onboarding Status</h2>
        </div>
      )}
      <div className={`onboarding-status-message ${statusConfig.className}`}>
        <StatusIcon size={20} className="status-icon" />
        <span className="status-text">{statusConfig.message}</span>
      </div>

      {itemsNeedingAttention.length > 0 && (
        <div className="onboarding-items-table-container">
          <h3 className="onboarding-items-title">Items Requiring Attention</h3>
          <table className="onboarding-items-table">
            <thead>
              <tr>
                <th>Blocking</th>
                <th>Type</th>
                <th>Item</th>
              </tr>
            </thead>
            <tbody>
              {itemsNeedingAttention.map((item) => (
                <tr key={item.id}>
                  <td>
                    <span className={item.isBlocking ? "blocking-badge blocking" : "blocking-badge non-blocking"}>
                      {item.isBlocking ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <span className="issue-type-label">
                      {(() => {
                        const IconComponent = getIssueTypeIcon(item.type);
                        return IconComponent ? (
                          <>
                            <IconComponent size={16} className="issue-type-icon" />
                            {getIssueTypeLabel(item.type)}
                          </>
                        ) : (
                          getIssueTypeLabel(item.type)
                        );
                      })()}
                    </span>
                  </td>
                  <td>
                    <span className="issue-name">{item.name}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Main component
export const AppPayCompanyOnboardingStatus = ({
  checkCompany,
  onResolve,
}: AppPayCompanyOnboardingStatusProps) => {
  return (
    <div className="app-pay-company-onboarding-status-wrapper">
      <OnboardingStatusContent 
        checkCompany={checkCompany}
        onResolve={onResolve}
      />
    </div>
  );
};

